import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

interface StateWiseTrendChartProps {
  data: Array<{
    stateID?: number;
    stateName: string;
    totalSchools: number;
    pmshriCount: number;
    nonPmshriCount: number;
    pmshriPercentage: string | number;
  }>;
  title: string;
  height?: number;
  sortBy?: 'name' | 'total' | 'pmshri' | 'percentage';
}

const StateWiseTrendChart: React.FC<StateWiseTrendChartProps> = ({
  data,
  title,
  height = 400,
  sortBy = 'total',
}) => {
  // Sort data based on sortBy parameter
  const sortedData = [...data].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.stateName.localeCompare(b.stateName);
      case 'pmshri':
        return b.pmshriCount - a.pmshriCount;
      case 'percentage':
        return parseFloat(String(b.pmshriPercentage)) - parseFloat(String(a.pmshriPercentage));
      case 'total':
      default:
        return b.totalSchools - a.totalSchools;
    }
  });

  // Take top 15 states for better visualization
  const chartData = sortedData.slice(0, 15).map((item) => ({
    name: item.stateName.length > 15 
      ? item.stateName.substring(0, 15) + '...' 
      : item.stateName,
    fullName: item.stateName,
    'PM-SHRI': item.pmshriCount,
    'Non PM-SHRI': item.nonPmshriCount,
    'Total': item.totalSchools,
    percentage: parseFloat(String(item.pmshriPercentage)) || 0,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800 mb-2">{item.fullName}</p>
          <div className="space-y-1">
            <p className="text-purple-600">
              Total: {item.Total.toLocaleString()}
            </p>
            <p className="text-green-600">
              PM-SHRI: {item['PM-SHRI'].toLocaleString()}
            </p>
            <p className="text-blue-600">
              Non PM-SHRI: {item['Non PM-SHRI'].toLocaleString()}
            </p>
            <p className="text-gray-500 text-sm border-t pt-1 mt-1">
              PM-SHRI Percentage: {item.percentage.toFixed(1)}%
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64 text-gray-400">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <span className="text-sm text-gray-500">
          Showing top {Math.min(15, data.length)} states
        </span>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
        >
          <defs>
            <linearGradient id="colorPmshri" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#22c55e" stopOpacity={0.1} />
            </linearGradient>
            <linearGradient id="colorNonPmshri" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            interval={0}
            height={80}
            tick={{ fontSize: 11, fill: '#666' }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: '#666' }}
            tickFormatter={(value) => value.toLocaleString()}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ paddingTop: 20 }} />
          <Area
            type="monotone"
            dataKey="PM-SHRI"
            stackId="1"
            stroke="#22c55e"
            fill="url(#colorPmshri)"
            strokeWidth={2}
          />
          <Area
            type="monotone"
            dataKey="Non PM-SHRI"
            stackId="1"
            stroke="#3b82f6"
            fill="url(#colorNonPmshri)"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default StateWiseTrendChart;
